<template>
    <v-card>
        <v-card-title>
            Kela-erä: {{ batch.fileName || batch.id }}
            <v-spacer></v-spacer>
            <v-btn
                color="primary"
                class="mr-2"
                @click="showTransportEvents"
            >
                <v-icon left>mdi-format-list-bulleted</v-icon>
                {{ $t('kelaBatch.showEvents') }}
            </v-btn>
            <v-btn icon @click="$emit('close')">
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-card-title>

        <v-card-text>
            <v-row>
                <!-- Batch perustiedot -->
                <v-col cols="12" md="6">
                    <h3 class="mb-3">{{ $t('kelaBatch.basicInfo') }}</h3>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <td><strong>ID</strong></td>
                                <td>
                                    {{ batch.id }}
                                    <v-btn icon x-small class="ml-1" @click="copyToClipboard(batch.id)" :title="$t('kelaBatch.copyToClipboard')">
                                        <v-icon x-small>mdi-content-copy</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                            <tr><td><strong>{{ $t('kelaBatch.region') }}</strong></td><td>{{ batch.region }}</td></tr>
                            <tr><td><strong>{{ $t('kelaBatch.year') }}</strong></td><td>{{ batch.year }}</td></tr>
                            <tr><td><strong>{{ $t('kelaBatch.tilitysNro') }}</strong></td><td>{{ batch.tilitysNro }}</td></tr>
                            <tr><td><strong>{{ $t('kelaBatch.siirtotunnus') }}</strong></td><td>{{ batch.siirtotunnus }}</td></tr>
                            <tr><td><strong>{{ $t('kelaBatch.fileName') }}</strong></td><td>{{ batch.fileName || '-' }}</td></tr>
                            <tr>
                                <td><strong>{{ $t('kelaBatch.sentAt') }}</strong></td>
                                <td>{{ formatDateTime(batch.sentAt) }}</td>
                            </tr>
                            <tr>
                                <td><strong>{{ $t('kelaBatch.containsCombinedTrips') }}</strong></td>
                                <td>
                                    <v-icon small :color="batch.containsCombinedTrips ? 'primary' : 'grey'">
                                        {{ batch.containsCombinedTrips ? 'mdi-call-merge' : 'mdi-minus' }}
                                    </v-icon>
                                    {{ batch.containsCombinedTrips ? $t('kelaBatch.containsCombinedYes') : $t('kelaBatch.containsCombinedNo') }}
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>

                <!-- Yhteenveto -->
                <v-col cols="12" md="6">
                    <h3 class="mb-3">{{ $t('kelaBatch.summary') }}</h3>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <td><strong>{{ $t('kelaBatch.transportEventCount') }}</strong></td>
                                <td>{{ batch.transportEventCount }}</td>
                            </tr>
                            <tr>
                                <td><strong>{{ $t('kelaBatch.totalCost') }}</strong></td>
                                <td>{{ formatCurrency(batch.totalCost) }}</td>
                            </tr>
                            <tr>
                                <td><strong>{{ $t('kelaBatch.totalReimbursement') }}</strong></td>
                                <td>{{ formatCurrency(batch.totalReimbursement) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <h3 class="mt-4 mb-3">{{ $t('kelaBatch.feedback') }}</h3>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <td><strong>{{ $t('kelaBatch.feedbackStatus') }}</strong></td>
                                <td>
                                    <v-chip small :color="feedbackStatusColor(batch.feedbackStatus)">
                                        {{ feedbackStatusLabel(batch.feedbackStatus) }}
                                    </v-chip>
                                </td>
                            </tr>
                            <tr>
                                <td><strong>{{ $t('kelaBatch.feedbackReceived') }}</strong></td>
                                <td>{{ batch.feedbackReceivedAt ? formatDateTime(batch.feedbackReceivedAt) : '-' }}</td>
                            </tr>
                            <tr>
                                <td><strong>{{ $t('kelaBatch.feedbackFile') }}</strong></td>
                                <td>{{ batch.feedbackFileName || '-' }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <h3 class="mt-4 mb-3">{{ $t('kelaBatch.settlement') }}</h3>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <td><strong>{{ $t('kelaBatch.settlementReceived') }}</strong></td>
                                <td>{{ batch.settlementReceivedAt ? formatDateTime(batch.settlementReceivedAt) : '-' }}</td>
                            </tr>
                            <tr>
                                <td><strong>{{ $t('kelaBatch.settlementFiles') }}</strong></td>
                                <td>
                                    <div v-if="batch.settlementFiles && batch.settlementFiles.length">
                                        <div v-for="f in batch.settlementFiles" :key="f.id">
                                            {{ f.fileName }}
                                            <span class="grey--text text--darken-1 ml-2">{{ formatDateTime(f.receivedAt) }}</span>
                                        </div>
                                    </div>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>

            <!-- Tapahtuma-IDt -->
            <v-row v-if="batch.transportEventIds && batch.transportEventIds.length > 0">
                <v-col cols="12">
                    <div class="d-flex align-center mb-2">
                        <h3>{{ $t('kelaBatch.eventIds') }} ({{ batch.transportEventIds.length }} kpl)</h3>
                        <v-btn
                            icon
                            small
                            class="ml-2"
                            @click="copyEventIds"
                            :title="$t('kelaBatch.copyToClipboard')"
                        >
                            <v-icon small>mdi-content-copy</v-icon>
                        </v-btn>
                    </div>
                    <div class="event-ids-container grey lighten-4 pa-2 rounded text-caption">
                        {{ batch.transportEventIds.join(', ') }}
                    </div>
                </v-col>
            </v-row>
        </v-card-text>

        <v-snackbar v-model="copySnackbar" timeout="2000" color="success">
            {{ $t('kelaBatch.copiedToClipboard') }}
        </v-snackbar>
    </v-card>
</template>

<script>
import moment from "moment";

export default {
    name: "KelaBatchDetailPanel",
    props: {
        batch: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            copySnackbar: false
        };
    },
    methods: {
        showTransportEvents() {
            this.$store.dispatch("transportEventTable/setKelaBatchFilter", this.batch.id);
            this.$router.push({ name: "Invoicing", query: { KelaBatchId: this.batch.id } });
        },
        formatDateTime(value) {
            if (!value) return "-";
            return moment(value).format("DD.MM.YYYY HH:mm");
        },
        formatCurrency(value) {
            if (value === null || value === undefined) return "-";
            return new Intl.NumberFormat("fi-FI", {
                style: "currency",
                currency: "EUR"
            }).format(value);
        },
        feedbackStatusColor(status) {
            if (!status) return "grey";
            if (status === "OK" || status.includes("Hyväksytty")) return "green";
            if (status.includes("Virhe") || status.includes("Error")) return "red";
            return "orange";
        },
        feedbackStatusLabel(status) {
            if (!status) return this.$t("kelaBatch.feedbackPending");
            if (status === "OK" || status.includes("Hyväksytty")) return status;
            if (status.includes("Virhe") || status.includes("Error")) return status;
            return status;
        },
        async copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                this.copySnackbar = true;
            } catch {
                // Clipboard API not available
            }
        },
        async copyEventIds() {
            if (!this.batch.transportEventIds) return;
            await this.copyToClipboard(this.batch.transportEventIds.join("\n"));
        }
    }
};
</script>

<style scoped>
.event-ids-container {
    max-height: 120px;
    overflow-y: auto;
    word-break: break-all;
    font-size: 0.75rem;
}
</style>
